import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, PencilIcon, TrashIcon, CubeIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Packaging = () => {
  const [packagingComponents, setPackagingComponents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    packaging_type: '',
    cost_per_unit: '',
    price_update_date: new Date().toISOString().split('T')[0],
    unit_of_measure: '',
    volume_ml: '',
    notes: ''
  });

  const packagingTypes = [
    { value: 'bottle', label: 'Bottiglia' },
    { value: 'cap', label: 'Tappo' },
    { value: 'capsule', label: 'Capsula' },
    { value: 'label_front', label: 'Etichetta Fronte' },
    { value: 'label_back', label: 'Etichetta Retro' },
    { value: 'box', label: 'Scatola/Cartone' },
    { value: 'divider', label: 'Divisore' },
    { value: 'other', label: 'Altro' }
  ];

  const unitOfMeasure = [
    { value: 'pieces', label: 'Pezzi' },
    { value: 'kg', label: 'Chilogrammi (kg)' },
    { value: 'g', label: 'Grammi (g)' },
    { value: 'ml', label: 'Millilitri (ml)' }
  ];

  useEffect(() => {
    loadPackagingComponents();
  }, []);

  const loadPackagingComponents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/packaging`);
      setPackagingComponents(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei componenti packaging:', error);
      toast.error('Errore nel caricamento dei componenti packaging');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        cost_per_unit: parseFloat(formData.cost_per_unit) || 0,
        volume_ml: formData.volume_ml ? parseFloat(formData.volume_ml) : null
      };

      if (editingComponent) {
        await axios.put(`${API}/packaging/${editingComponent.id}`, payload);
        toast.success('Componente packaging aggiornato con successo');
      } else {
        await axios.post(`${API}/packaging`, payload);
        toast.success('Componente packaging creato con successo');
      }

      await loadPackagingComponents();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio del componente packaging');
    }
  };

  const handleEdit = (component) => {
    setEditingComponent(component);
    setFormData({
      code: component.code,
      name: component.name,
      packaging_type: component.packaging_type,
      cost_per_unit: component.cost_per_unit.toString(),
      price_update_date: component.price_update_date ? new Date(component.price_update_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      unit_of_measure: component.unit_of_measure,
      volume_ml: component.volume_ml?.toString() || '',
      notes: component.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo componente?')) {
      return;
    }

    try {
      await axios.delete(`${API}/packaging/${id}`);
      toast.success('Componente eliminato con successo');
      await loadPackagingComponents();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione del componente');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      packaging_type: '',
      cost_per_unit: '',
      price_update_date: new Date().toISOString().split('T')[0],
      unit_of_measure: '',
      volume_ml: '',
      notes: ''
    });
    setEditingComponent(null);
  };

  const getPackagingTypeBadge = (type) => {
    const typeConfig = {
      bottle: { variant: 'default', label: 'Bottiglia', color: 'bg-blue-100 text-blue-800' },
      cap: { variant: 'secondary', label: 'Tappo', color: 'bg-gray-100 text-gray-800' },
      capsule: { variant: 'outline', label: 'Capsula', color: 'bg-purple-100 text-purple-800' },
      label_front: { variant: 'default', label: 'Etichetta F.', color: 'bg-green-100 text-green-800' },
      label_back: { variant: 'secondary', label: 'Etichetta R.', color: 'bg-green-100 text-green-800' },
      box: { variant: 'outline', label: 'Scatola', color: 'bg-amber-100 text-amber-800' },
      divider: { variant: 'secondary', label: 'Divisore', color: 'bg-slate-100 text-slate-800' },
      other: { variant: 'outline', label: 'Altro', color: 'bg-gray-100 text-gray-800' }
    };

    const config = typeConfig[type] || typeConfig.other;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="packaging-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Packaging</h1>
          <p className="text-gray-600">Gestisci i componenti di packaging per i prodotti</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-packaging-btn">
              <PlusIcon className="w-4 h-4 mr-2" />
              Nuovo Componente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingComponent ? 'Modifica Componente' : 'Nuovo Componente Packaging'}
              </DialogTitle>
              <DialogDescription>
                Inserisci i dettagli del componente packaging. I campi con * sono obbligatori.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Codice *</Label>
                  <Input
                    id="code"
                    data-testid="packaging-code-input"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Es. BOT-750"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    data-testid="packaging-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Es. Bottiglia 750ml"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="packaging_type">Tipo Componente *</Label>
                  <Select
                    value={formData.packaging_type}
                    onValueChange={(value) => setFormData({...formData, packaging_type: value})}
                  >
                    <SelectTrigger data-testid="packaging-type-select">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {packagingTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit_of_measure">Unità di Misura *</Label>
                  <Select
                    value={formData.unit_of_measure}
                    onValueChange={(value) => setFormData({...formData, unit_of_measure: value})}
                  >
                    <SelectTrigger data-testid="packaging-unit-select">
                      <SelectValue placeholder="Seleziona unità" />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOfMeasure.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cost_per_unit">Costo per Unità (€) *</Label>
                  <Input
                    id="cost_per_unit"
                    data-testid="packaging-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({...formData, cost_per_unit: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_update_date">Data Aggiornamento Prezzo *</Label>
                  <Input
                    id="price_update_date"
                    data-testid="packaging-price-date-input"
                    type="date"
                    value={formData.price_update_date}
                    onChange={(e) => setFormData({...formData, price_update_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="volume_ml">Volume (ml) - Solo per Bottiglie</Label>
                  <Input
                    id="volume_ml"
                    data-testid="packaging-volume-input"
                    type="number"
                    min="0"
                    value={formData.volume_ml}
                    onChange={(e) => setFormData({...formData, volume_ml: e.target.value})}
                    placeholder="Es. 750"
                    disabled={formData.packaging_type !== 'bottle'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Input
                  id="notes"
                  data-testid="packaging-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive sul componente"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  data-testid="cancel-packaging-btn"
                >
                  Annulla
                </Button>
                <Button type="submit" data-testid="save-packaging-btn">
                  {editingComponent ? 'Aggiorna' : 'Salva'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Input
              type="text"
              placeholder="🔍 Cerca per nome o codice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-4"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              {packagingComponents.filter(pkg => 
                pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                pkg.code.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} risultati trovati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Components List */}
      {packagingComponents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CubeIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun componente packaging</h3>
            <p className="text-gray-500 text-center mb-6">
              Inizia creando il tuo primo componente di packaging per i prodotti.
            </p>
            <Button onClick={() => setIsModalOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Crea primo componente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {packagingComponents
            .filter(pkg => 
              pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              pkg.code.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((component) => (
            <Card key={component.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <CardTitle className="text-lg">{component.name}</CardTitle>
                      {getPackagingTypeBadge(component.packaging_type)}
                    </div>
                    <CardDescription>
                      Codice: {component.code} • {formatCurrency(component.cost_per_unit)}/{component.unit_of_measure}
                      {component.volume_ml && (
                        <> • {component.volume_ml}ml</>
                      )}
                      <br />
                      Aggiornamento prezzo: {new Date(component.price_update_date).toLocaleDateString('it-IT')}
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(component)}
                      data-testid={`edit-packaging-${component.id}`}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(component.id)}
                      data-testid={`delete-packaging-${component.id}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {component.notes && (
                <CardContent>
                  <p className="text-sm text-gray-600">{component.notes}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Packaging;